import React, { useMemo, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from '@/features/shared/components/ui/sidebar';
import { useAppSelector } from '@/app/hooks';
import { useDelegatedCapabilities } from '@/features/delegation/useDelegatedCapabilities';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Wallet, Coins, HeartPulse, Clock, Boxes, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { ADMIN_GROUPS, ADMIN_GROUP_FALLBACK } from '@/features/navigation/adminNav';
import { getBenefitsSettings } from '@/lib/hrApi';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useMarketplaceVisible } from '@/features/marketplace/useMarketplaceVisible';
import { useFilteredNavigation } from '@/features/navigation/hooks/useFilteredNavigation';
import { useManagementNavigation } from '@/features/navigation/hooks/useManagementNavigation';
import { useDashboardPreference } from '@/features/navigation/hooks/useDashboardPreference';
import { DashboardSelector } from '@/features/navigation/components/DashboardSelector';
import { KaziPeopleLogo } from '@/features/shared/components/branding/KaziPeopleLogo';
import { isPathActive } from '@/features/navigation/utils';
import type { UserContext, UserRole, Permission, FeatureFlag, NavigationGroup } from '@/features/navigation/types';

// Administration IA (see docs/admin-nav-redesign.md): render the Administration section as
// collapsible groups instead of one flat list. Flip to false to A/B against the old flat
// list during local review.
const GROUPED_ADMIN_NAV = true;
const ADMIN_GROUPS_STORAGE_KEY = 'kp-admin-groups';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What to print after "Workspace:" — the company name, else a human-readable slug, else
 *  nothing. A bare tenant uuid is never a label. */
export function workspaceName(tenantName?: string | null, tenantSlug?: string | null): string {
  const name = (tenantName || '').trim();
  if (name) return name;
  const slug = (tenantSlug || '').trim();
  return slug && !UUID_RE.test(slug) ? slug : '';
}

interface TenantAwareNavigationSidebarProps {
  userRole?: UserRole;
  userId?: string;
  tenantSlug?: string;
  permissions?: Permission[];
  featureFlags?: FeatureFlag[];
  /** Desktop icons-only collapse (md+). Managed by the layout + persisted. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const TenantAwareNavigationSidebar: React.FC<TenantAwareNavigationSidebarProps> = ({
  userRole = 'employee',
  userId = 'default-user',
  tenantSlug,
  permissions = [],
  featureFlags = [],
  collapsed = false,
  onToggleCollapse,
}) => {
  const { t } = useTranslation(['navigation', 'common']);
  // The workspace line names the COMPANY. `tenantSlug` is the URL segment, which is the
  // tenant's uuid for workspaces without a slug — never show that to a person; prefer the
  // tenant name, fall back to a real slug, and otherwise show nothing.
  const tenantName = useAppSelector((s) => s.tenant?.current?.name);
  const workspaceLabel = workspaceName(tenantName, tenantSlug);
  const location = useLocation();
  const navigate = useNavigate();
  // Mobile off-canvas drawer state (from SidebarProvider). Auto-close after a
  // navigation so tapping a link on a phone dismisses the drawer.
  const { openMobile, setOpenMobile } = useSidebar();
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);
  // The manager/admin nav sections (Administration, Payroll, Attendance) are
  // built by a shared, RBAC-gated hook so the global command palette (⌘K) indexes
  // exactly the same permission-filtered set the sidebar renders. See
  // useManagementNavigation for the gate rationale on each item.
  const { adminItems, payrollItems, attendanceItems, showAdmin, showPayroll, showAttendance } =
    useManagementNavigation();
  // Employee "My Benefits" shows only when the tenant enables the employee view.
  const [showMyBenefits, setShowMyBenefits] = useState(false);
  useEffect(() => {
    getBenefitsSettings()
      .then((s) => {
        const anyFeature = !!s.benefits_enabled || !!s.allowances_enabled || !!s.deductions_enabled;
        setShowMyBenefits(anyFeature && !!s.show_employee_view);
      })
      .catch(() => { setShowMyBenefits(false); });
  }, []);
  // Accurate role label (the userRole prop is only the binary nav tier).
  const { isTenantOwner, isHrManager, isHrStaff, isManager, isFinance } = useRoleAccess();
  // Dynamic product visibility (E4): show a product area only when the tenant is entitled.
  // Fails open for un-provisioned tenants, so existing tenants are unaffected. UX only —
  // the gateway + services enforce.
  const { hasProduct } = useEntitlements();
  // Employees only see "My Products" when the employer's marketplace toggle is on AND
  // ≥1 product is switched on for them; "Loans & Advances" follows the employer's
  // loans toggle. Both computed server-side (credit.tenant_settings).
  const { visible: marketplaceVisible, loansVisible } = useMarketplaceVisible(hasProduct('credit'));
  const roleLabel = isTenantOwner ? t('common:owner', 'Owner')
    : isHrManager ? t('common:hr_admin', 'HR Admin')
    : isHrStaff ? t('common:hr_staff', 'HR Staff')
    : isFinance ? t('common:finance', 'Finance')
    : isManager ? t('common:manager')
    : t('common:employee');

  // Capabilities delegated to this user right now: they open the delegable
  // entries (e.g. Leave Approvals for someone covering a manager, NA-3).
  const { capabilities: delegatedCaps } = useDelegatedCapabilities();

  // Create user context
  const userContext: UserContext = useMemo(() => ({
    userId,
    role: userRole,
    permissions: permissions.length > 0 ? permissions : getDefaultPermissions(userRole),
    delegatedPermissions: delegatedCaps as Permission[],
    tenantSlug,
    featureFlags
  }), [userId, userRole, permissions, delegatedCaps, tenantSlug, featureFlags]);

  // Dashboard preference management
  const {
    preferredDashboardId,
    setPreferredDashboard,
    isLoading: isDashboardLoading
  } = useDashboardPreference({
    userId,
    tenantSlug,
    // Optional: Add API handler when backend is ready
    // onPreferenceChange: createDashboardPreferenceApiHandler('/api')
  });

  // Get filtered navigation
  const {
    navigationGroups,
    availableDashboards,
    currentDashboard,
    buildPath
  } = useFilteredNavigation({
    userContext,
    preferredDashboardId
  });

  // Render order: insert the Administration section right after Team Management
  // (i.e. before the Support group), not at the very bottom.
  const ADMIN_SENTINEL = { id: '__admin__' } as NavigationGroup;
  const PAYROLL_SENTINEL = { id: '__payroll__' } as NavigationGroup;
  const ATTENDANCE_SENTINEL = { id: '__attendance__' } as NavigationGroup;
  const orderedGroups: NavigationGroup[] = React.useMemo(() => {
    // Inject "My Benefits" into the personal group (the one with My Pay) when the
    // tenant lets employees see their benefits.
    let groups = navigationGroups;
    if (showMyBenefits) {
      groups = navigationGroups.map((g) => {
        if (!g.items?.some((it) => it.id === 'my-pay') || g.items.some((it) => it.id === 'my-benefits')) return g;
        return { ...g, items: [...g.items, { id: 'my-benefits', label: 'My Benefits', icon: HeartPulse, path: 'my-benefits', requiredRoles: ['employee', 'manager'] } as any] };
      });
    }
    // Trust Center (privacy, consents, partner data-sharing, access log) — every
    // employee has data rights, so this is unconditional.
    groups = groups.map((g) => {
      if (!g.items?.some((it) => it.id === 'my-pay') || g.items.some((it) => it.id === 'trust-center')) return g;
      return { ...g, items: [...g.items, { id: 'trust-center', label: 'Trust Center', icon: ShieldCheck, path: 'privacy', requiredRoles: ['employee', 'manager'] } as any] };
    });
    // Self-service loans & advances: any employee can request for themselves — but only
    // when the tenant actually has the credit product (gate parity with the employer nav
    // above; the gateway also blocks /credit/* without the module).
    groups = groups.map((g) => {
      if (!hasProduct('credit') || !g.items?.some((it) => it.id === 'my-pay') || g.items.some((it) => it.id === 'my-loans')) return g;
      const items = [...g.items];
      // "My Products" only appears when the tenant has enabled ≥1 product for employees
      // (and the employer's marketplace toggle is on); "Loans & Advances" follows the
      // employer's loans toggle. Employee-facing only — admin surfaces are never gated here.
      if (marketplaceVisible) items.push({ id: 'my-products', label: 'My Products', icon: Boxes, path: 'my-products' } as any);
      if (loansVisible) items.push({ id: 'my-loans', label: 'My Loans', icon: Coins, path: 'loans' } as any);
      return { ...g, items };
    });
    const extra: NavigationGroup[] = [];
    if (showAttendance) extra.push(ATTENDANCE_SENTINEL);
    if (showPayroll) extra.push(PAYROLL_SENTINEL);
    if (showAdmin) extra.push(ADMIN_SENTINEL);
    if (extra.length === 0) return groups;
    const supportIdx = groups.findIndex((g) => g.id === 'support');
    if (supportIdx < 0) return [...groups, ...extra];
    return [
      ...groups.slice(0, supportIdx),
      ...extra,
      ...groups.slice(supportIdx),
    ];
  }, [navigationGroups, showAdmin, showPayroll, showAttendance, showMyBenefits, marketplaceVisible, loansVisible, hasProduct]);

  // "HR team only": HR Action Center, Surveys and HR Action Settings are HR tools.
  // They already require performance:manage (plain line managers lack it), but per
  // product decision we also gate them to HR roles explicitly — Owner/admin keep
  // access. Applied to both the render and the active-path resolver so they agree.
  const hrEligible = isTenantOwner || isHrManager || isHrStaff || showAdmin || userRole === 'system-admin';
  const visibleGroups: NavigationGroup[] = React.useMemo(() => {
    if (hrEligible) return orderedGroups;
    const HR_ONLY = new Set(['hr-actions', 'surveys', 'hr-action-settings']);
    return orderedGroups
      .map((g) => (g.items ? { ...g, items: g.items.filter((it) => !HR_ONLY.has(it.id)) } : g))
      .filter((g) => g.id.startsWith('__') || !g.items || g.items.length > 0);
  }, [orderedGroups, hrEligible]);

  // Handle dashboard selection
  const handleDashboardSelect = async (dashboardId: string) => {
    await setPreferredDashboard(dashboardId);
    const dashboard = availableDashboards.find(d => d.id === dashboardId);
    if (dashboard) {
      navigate(buildPath(dashboard.path));
    }
  };

  // Every nav path currently rendered (main groups + their children, plus the admin,
  // payroll and attendance sections). Used to resolve the SINGLE most-specific active
  // item so a parent path never stays highlighted alongside its more-specific sibling
  // (e.g. `hr-actions` was lighting up on `hr-actions/settings`).
  const allNavPaths = React.useMemo(() => {
    const out: string[] = [];
    for (const g of visibleGroups) {
      for (const it of (g.items ?? [])) {
        if (it.path) out.push(it.path);
        for (const c of ((it as any).children ?? [])) if (c?.path) out.push(c.path);
      }
    }
    for (const it of [...adminItems, ...payrollItems, ...(attendanceItems ?? [])]) if (it.path) out.push(it.path);
    return out;
  }, [visibleGroups, adminItems, payrollItems, attendanceItems]);

  // isPathActive prefix-matches, so several ancestor paths match at once. The real active
  // item is the longest matching path; compute it once and compare against it.
  const activeNavPath = React.useMemo(
    () =>
      allNavPaths
        .filter((p) => isPathActive(location.pathname, p, tenantSlug, userRole === 'system-admin'))
        .sort((a, b) => b.length - a.length)[0],
    [allNavPaths, location.pathname, tenantSlug, userRole],
  );

  const isActive = (path: string) => !!activeNavPath && path === activeNavPath;

  // Single, route-derived active resolver. isPathActive prefix-matches, so several sibling
  // admin paths can match at once (e.g. `settings`, `settings/automation`, `settings/payments`
  // on /settings/automation) — the most specific (longest) match is the real one. Memoized on
  // the pathname so it can never go stale (the reported "wrong item highlighted" bug).
  const activeAdminPath = useMemo(
    () =>
      adminItems
        .filter((it) => isActive(it.path))
        .map((it) => it.path)
        .sort((a, b) => b.length - a.length)[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminItems, location.pathname, tenantSlug, userRole],
  );

  // Same route-derived, longest-match resolver for the Payroll section. Replaces the previous
  // bespoke `payroll`-prefix workaround: at /payroll/loans both `payroll` and `payroll/loans`
  // match, so the longest (most specific) wins; on a run detail (/payroll/runs/:id) only
  // `payroll` matches, so Payroll Runs stays lit.
  const activePayrollPath = useMemo(
    () =>
      payrollItems
        .filter((it) => isActive(it.path))
        .map((it) => it.path)
        .sort((a, b) => b.length - a.length)[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payrollItems, location.pathname, tenantSlug, userRole],
  );

  // Group the visible Administration items into collapsible sections (presentation only —
  // every item keeps its exact permission/entitlement gate from adminItems). Groups with no
  // visible items are dropped, so a user only ever sees groups they have access to.
  const adminGroups = useMemo(() => {
    const byId = new Map(adminItems.map((it) => [it.id, it]));
    const used = new Set<string>();
    const groups = ADMIN_GROUPS.map((g) => {
      const items = g.itemIds
        .map((id) => byId.get(id))
        .filter((it): it is (typeof adminItems)[number] => Boolean(it));
      items.forEach((it) => used.add(it.id));
      return { id: g.id, label: g.label, icon: g.icon, items };
    });
    // Safety net: any admin item not explicitly mapped goes to the fallback group so it is
    // never silently dropped from the sidebar.
    const unmapped = adminItems.filter((it) => !used.has(it.id));
    if (unmapped.length) {
      const fb = groups.find((g) => g.id === ADMIN_GROUP_FALLBACK);
      if (fb) fb.items.push(...unmapped);
    }
    return groups.filter((g) => g.items.length > 0);
  }, [adminItems]);

  // Which group (if any) owns the currently-active admin route — used to auto-expand it.
  const activeAdminGroupId = useMemo(
    () => adminGroups.find((g) => g.items.some((it) => it.path === activeAdminPath))?.id,
    [adminGroups, activeAdminPath],
  );

  // Expanded-group state: user toggles win and persist; otherwise only the group containing
  // the active route is open by default (keeps the sidebar short).
  const [expandedAdminGroups, setExpandedAdminGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(ADMIN_GROUPS_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const isAdminGroupOpen = (groupId: string) =>
    groupId in expandedAdminGroups ? expandedAdminGroups[groupId] : groupId === activeAdminGroupId;
  const toggleAdminGroup = (groupId: string) =>
    setExpandedAdminGroups((prev) => {
      const next = { ...prev, [groupId]: !(groupId in prev ? prev[groupId] : groupId === activeAdminGroupId) };
      try {
        localStorage.setItem(ADMIN_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

  return (
    <>
      {/* Mobile backdrop — tap to dismiss the drawer (hidden on md+). */}
      {openMobile && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 md:hidden"
          onClick={() => setOpenMobile(false)}
          aria-hidden="true"
        />
      )}
      <aside
        data-collapsed={collapsed}
        className={`bg-sidebar text-white border-r border-white/5 h-screen flex flex-col shadow-xl fixed left-0 top-0 z-[60] transition-all duration-300 ease-in-out md:translate-x-0 print:hidden ${
          collapsed ? 'w-[76px]' : 'w-64'
        } ${openMobile ? 'translate-x-0' : '-translate-x-full'}`}
      >
      {/* Logo Section + collapse toggle */}
      <div className={`border-b border-white/10 ${collapsed ? 'px-3 py-6' : 'p-6'}`}>
        <div className="flex items-center justify-between gap-2">
          <KaziPeopleLogo size={30} wordmarkColor="#FFFFFF" wordmark={!collapsed} />
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden md:flex items-center justify-center rounded-md p-1.5 text-white/50 hover:bg-white/10 hover:text-white transition-colors"
            >
              {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          )}
        </div>

        {!collapsed && (
          <>
            <div className="mt-4 space-y-2">
              {workspaceLabel && (
                <p className="text-sm text-orange-400/80 font-medium" title={workspaceLabel}>
                  {t('navigation:workspace')}: {workspaceLabel}
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/50">{t('navigation:logged_in_as')}</span>
                <span className="text-xs px-2 py-1 bg-orange-500/15 text-orange-400 rounded-md font-medium">
                  {roleLabel}
                </span>
              </div>
            </div>

            {/* Dashboard Selector */}
            {availableDashboards.length > 1 && (
              <div className="mt-4">
                <DashboardSelector
                  availableDashboards={availableDashboards}
                  currentDashboard={currentDashboard}
                  onSelectDashboard={handleDashboardSelect}
                  isLoading={isDashboardLoading}
                />
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Navigation */}
      <nav className={`flex-1 py-6 overflow-y-auto ${collapsed ? 'px-2' : 'px-4'}`}>
        {visibleGroups.map((group) => {
          // Time & Attendance — hardcoded section gated by canonical RBAC
          // (managers+ review attendance; HR/owners edit the policy).
          if (group.id === '__attendance__') {
            return (
              <div key="__attendance__" className="mt-6 pt-4 border-t border-white/10">
                <h3 className="px-3 mb-1.5 text-[10px] font-bold text-white/30 uppercase tracking-[0.12em] flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {t('navigation:group_attendance', 'Time & Attendance')}
                </h3>
                <ul className="space-y-2">
                  {attendanceItems.map((item) => {
                    const AttendanceIcon = item.icon;
                    const itemActive = isActive(item.path);
                    return (
                      <li key={item.id}>
                        <Link
                          to={buildPath(item.path)}
                          className={`group flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 ${
                            itemActive
                              ? 'bg-orange-500/[0.16] text-orange-300 border-l-2 border-orange-500'
                              : 'border-l-2 border-transparent text-white/60 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <AttendanceIcon className={`w-5 h-5 transition-all duration-300 ${itemActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                          <span className="flex-1">{t(`navigation:${item.id}`, item.label)}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }

          // Payroll operations — hardcoded section gated by canonical RBAC.
          if (group.id === '__payroll__') {
            return (
              <div key="__payroll__" className="mt-6 pt-4 border-t border-white/10">
                <h3 className="px-3 mb-1.5 text-[10px] font-bold text-white/30 uppercase tracking-[0.12em] flex items-center gap-1.5">
                  <Wallet className="w-3 h-3" />
                  {t('navigation:group_payroll', 'Payroll')}
                </h3>
                <ul className="space-y-2">
                  {payrollItems.map((item) => {
                    const PayrollIcon = item.icon;
                    // Longest-match resolver (activePayrollPath) handles the `payroll` prefix
                    // overlap uniformly — see the useMemo above.
                    const itemActive = item.path === activePayrollPath;
                    return (
                      <li key={item.id}>
                        <Link
                          to={buildPath(item.path)}
                          className={`group flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 ${
                            itemActive
                              ? 'bg-orange-500/[0.16] text-orange-300 border-l-2 border-orange-500'
                              : 'border-l-2 border-transparent text-white/60 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <PayrollIcon className={`w-5 h-5 transition-all duration-300 ${itemActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                          <span className="flex-1">{t(`navigation:${item.id.replace(/-/g, '_')}`, item.label)}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }

          // Company Administration (Model A) — positioned after Team Management.
          // Each item is gated by its own canonical RBAC permission (in adminItems).
          if (group.id === '__admin__') {
            const renderAdminLink = (item: (typeof adminItems)[number]) => {
              const AdminIcon = item.icon;
              const itemActive = item.path === activeAdminPath;
              return (
                <li key={item.id}>
                  <Link
                    to={buildPath(item.path)}
                    aria-current={itemActive ? 'page' : undefined}
                    title={collapsed ? t(`navigation:${item.id.replace(/-/g, '_')}`, item.label) : undefined}
                    className={`group flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 ${
                      itemActive
                        ? 'bg-orange-500/[0.16] text-orange-300 border-l-2 border-orange-500'
                        : 'border-l-2 border-transparent text-white/60 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <AdminIcon className={`w-5 h-5 transition-all duration-300 ${itemActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                    {!collapsed && <span className="flex-1">{t(`navigation:${item.id.replace(/-/g, '_')}`, item.label)}</span>}
                  </Link>
                </li>
              );
            };

            return (
              <div key="__admin__" className="mt-6 pt-4 border-t border-white/10">
                <h3 className="px-3 mb-1.5 text-[10px] font-bold text-white/30 uppercase tracking-[0.12em] flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  {!collapsed && t('navigation:group_administration', 'Administration')}
                </h3>

                {/* Collapsed (icons-only) sidebar OR toggle off → flat list, unchanged behaviour. */}
                {(collapsed || !GROUPED_ADMIN_NAV) ? (
                  <ul className="space-y-2">{adminItems.map(renderAdminLink)}</ul>
                ) : (
                  <div className="space-y-1">
                    {adminGroups.map((grp) => {
                      const GroupIcon = grp.icon;
                      const open = isAdminGroupOpen(grp.id);
                      const groupHasActive = grp.id === activeAdminGroupId;
                      const panelId = `admin-group-${grp.id}`;
                      return (
                        <div key={grp.id}>
                          <button
                            type="button"
                            onClick={() => toggleAdminGroup(grp.id)}
                            aria-expanded={open}
                            aria-controls={panelId}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-[10px] text-[12px] font-semibold transition-colors ${
                              groupHasActive ? 'text-orange-300' : 'text-white/70 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            <GroupIcon className="w-4 h-4 shrink-0" />
                            <span className="flex-1 text-left">{t(`navigation:admin_group_${grp.id}`, grp.label)}</span>
                            {groupHasActive && !open && (
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" aria-hidden="true" />
                            )}
                            {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                          </button>
                          {open && (
                            <ul id={panelId} className="mt-1 mb-1 ml-3 pl-2 border-l border-white/10 space-y-1">
                              {grp.items.map(renderAdminLink)}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const showGroupLabel = group.id !== 'main' || group.items.length > 1;
          
          return (
            <div key={group.id} className={group.id !== 'main' ? 'mt-6' : ''}>
              {showGroupLabel && (
                <h3 className="px-3 mb-1.5 text-[10px] font-bold text-white/30 uppercase tracking-[0.12em]">
                  {t(`navigation:group_${group.id}`, group.label)}
                </h3>
              )}
              <ul className="space-y-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const itemPath = buildPath(item.path);
                  const isItemActive = isActive(item.path);
                  
                  // Special handling for Help & Support to add separator
                  if (item.id === 'help') {
                    return (
                      <li key={item.id} className="mt-6 pt-4 border-t border-white/10">
                        <Link
                          to={itemPath}
                          className={`group flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 ${
                            isItemActive
                              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                              : 'text-white/70 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <Icon className={`w-5 h-5 transition-all duration-300 ${isItemActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                          <span className="flex-1">{t(`navigation:${item.id.replace(/-/g, '_')}`, item.label)}</span>
                          {item.badge && (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              item.badge.variant === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                              item.badge.variant === 'error' ? 'bg-red-100 text-red-700' :
                              item.badge.variant === 'success' ? 'bg-green-100 text-green-700' :
                              'bg-muted text-foreground'
                            }`}>
                              {item.badge.count}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  }
                  
                  return (
                    <li key={item.id}>
                      <Link
                        to={itemPath}
                        className={`group flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 ${
                          isItemActive
                            ? 'bg-orange-500/[0.16] text-orange-300 border-l-2 border-orange-500'
                            : 'border-l-2 border-transparent text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Icon className={`w-5 h-5 transition-all duration-300 ${isItemActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                        <span className="flex-1">{t(`navigation:${item.id.replace(/-/g, '_')}`, item.label)}</span>
                        {item.badge && (
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            item.badge.variant === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                            item.badge.variant === 'error' ? 'bg-red-100 text-red-700' :
                            item.badge.variant === 'success' ? 'bg-green-100 text-green-700' :
                            'bg-muted text-foreground'
                          }`}>
                            {item.badge.count}
                          </span>
                        )}
                      </Link>
                      
                      {/* Render children if any */}
                      {item.children && item.children.length > 0 && (
                        <ul className="mt-2 ml-8 space-y-1">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            const childPath = buildPath(child.path);
                            const isChildActive = isActive(child.path);
                            
                            return (
                              <li key={child.id}>
                                <Link
                                  to={childPath}
                                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                                    isChildActive
                                      ? 'bg-orange-500/15 text-orange-400'
                                      : 'text-white/55 hover:bg-white/5 hover:text-white/90'
                                  }`}
                                >
                                  <ChildIcon className="w-4 h-4" />
                                  <span>{t(`navigation:${child.id.replace(/-/g, '_')}`, child.label)}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Powered by KaziPeople */}
      <div className={`border-t border-white/10 ${collapsed ? 'p-2' : 'p-4'}`}>
        <div className={`flex items-center justify-center bg-white/5 rounded-xl border border-white/10 ${collapsed ? 'p-2' : 'space-x-2 px-4 py-3'}`}>
          <KaziPeopleLogo size={26} wordmark={false} />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-xs font-bold text-white/90">
                {t('navigation:powered_by')} Kazi<span className="text-orange-400">People</span>
              </span>
              <span className="text-[10px] text-white/45">
                {t('navigation:hr_management_system')}
              </span>
            </div>
          )}
        </div>
      </div>
      </aside>
    </>
  );
};

/**
 * Get default permissions based on role
 * This is a fallback - ideally permissions should come from backend
 */
function getDefaultPermissions(role: UserRole): Permission[] {
  switch (role) {
    case 'system-admin':
      return [
        'view_dashboard',
        'manage_system',
        'view_monitoring',
        'manage_security',
        'manage_tenant'
      ];
    case 'tenant-admin':
      return [
        'view_dashboard',
        'manage_users',
        'manage_team',
        'view_reports',
        'manage_leave',
        'view_employees',
        'manage_payroll',
        'view_analytics',
        'manage_settings'
      ];
    case 'manager':
      return [
        'view_dashboard',
        'manage_team',
        'view_reports',
        'manage_leave',
        'view_employees'
      ];
    case 'employee':
    default:
      return [
        'view_dashboard',
        'view_employees'
      ];
  }
}